import * as React from 'react'
import { SettingsSectionWithTabs } from '../../components/SettingsSectionWithTabs'
import { PrintoutConfigurationModel } from '@/lib/api/company/configuration/printout/types'
import { FieldsetInput } from '../../components/FieldsetInput'
import { FieldsetSwitch } from '../../components/FieldsetSwitch'
import { FieldsetTextarea } from '../../components/FieldsetTextarea'
import { usePersistentTab } from '@/modules/shared/stores/tabsStore'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type Props = {
  isSaving: boolean
  state: PrintoutConfigurationModel
  setState: React.Dispatch<React.SetStateAction<PrintoutConfigurationModel>>
}

export const PrintoutForm = ({ isSaving, state, setState }: Props) => {
  const { t } = useLocalize()
  const [selectedTab, setSelectedTab] = usePersistentTab(
    'settings-printout',
    'a',
  )

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, type, checked } = e.target

      switch (type) {
        case 'number':
          setState((state) => ({
            ...state,
            [name]: type === 'number' ? Number(value) : value,
          }))
          break
        case 'checkbox':
          setState((state) => ({
            ...state,
            [name]: checked,
          }))
          break
        default:
          setState((state) => ({
            ...state,
            [name]: value,
          }))
          break
      }
    },
    [setState],
  )

  const handleOtherEmploymentConditionsChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { name, value } = e.target
      setState((state) => ({
        ...state,
        [name]: value,
      }))
    },
    [setState],
  )

  return (
    <SettingsSectionWithTabs
      selected={selectedTab}
      setSelected={setSelectedTab}
    >
      <div
        data-tab-name="a"
        title={t('configuration-printout.tab-settings')}
        className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
      >
        <FieldsetSwitch
          label={t('configuration-printout.show-generation-date')}
          checked={state.printoutGenerationDate}
          name="printoutGenerationDate"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
        />

        <FieldsetSwitch
          label={t('configuration-printout.show-company-name')}
          checked={state.printoutCompanyName}
          name="printoutCompanyName"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
        />

        <FieldsetSwitch
          label={t('configuration-printout.add-driver-name-signature')}
          checked={state.printoutDriverName}
          name="printoutDriverName"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
        />

        <FieldsetSwitch
          label={t('configuration-printout.separate-pdf-files')}
          checked={state.printoutSingleGeneratedFileForEntity}
          name="printoutSingleGeneratedFileForEntity"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
        />

        <FieldsetSwitch
          label={t('configuration-printout.print-trip-vertically')}
          checked={state.printTripVertically}
          name="printTripVertically"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
        />

        <FieldsetSwitch
          label={t('configuration-printout.even-pages')}
          checked={state.evenPagesTimesheetPrint}
          name="evenPagesTimesheetPrint"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
        />

        <FieldsetSwitch
          label={t('configuration-printout.extended-margins')}
          checked={state.extendPrintMargins}
          name="extendPrintMargins"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
        />

        <FieldsetSwitch
          label={t('configuration-printout.group-page-numbers')}
          checked={state.groupPageNumbersByDrivers}
          name="groupPageNumbersByDrivers"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
        />

        <FieldsetInput
          label={t('configuration-printout.drivers-count-threshold')}
          value={state.driversCountForDocumentGeneration.toString()}
          name="driversCountForDocumentGeneration"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
          type="number"
        />
      </div>
      <div
        data-tab-name="b"
        title={t('configuration-printout.tab-contract-print')}
        className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
      >
        <FieldsetSwitch
          label={t('configuration-printout.print-employment-conditions')}
          checked={state.printEmploymentConditionsInfo}
          name="printEmploymentConditionsInfo"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
        />

        <FieldsetTextarea
          label={t('configuration-printout.other-employment-conditions-text')}
          name="otherEmploymentConditionsText"
          value={state.otherEmploymentConditionsText}
          placeholder={t(
            'configuration-printout.other-employment-conditions-text-placeholder',
          )}
          onChange={handleOtherEmploymentConditionsChange}
          disabled={isSaving}
          rows={5}
        />

        <FieldsetSwitch
          label={t('configuration-printout.print-working-time-declaration')}
          checked={state.printWorkingTimeDeclaration}
          name="printWorkingTimeDeclaration"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
        />

        <FieldsetSwitch
          label={t('configuration-printout.print-equal-treatment')}
          checked={state.printEqualTreatmentDeclaration}
          name="printEqualTreatmentDeclaration"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
        />

        <FieldsetSwitch
          label={t('configuration-printout.print-risk-declaration')}
          checked={state.printRiskDeclaration}
          name="printRiskDeclaration"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
        />

        <FieldsetSwitch
          label={t('configuration-printout.print-no-ban-declaration')}
          checked={state.printNoBanDeclaration}
          name="printNoBanDeclaration"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
        />

        <FieldsetSwitch
          label={t('configuration-printout.print-foreigner-declaration')}
          checked={state.printForeignerDeclaration}
          name="printForeignerDeclaration"
          placeholder=""
          onChange={handleInputChange}
          disabled={isSaving}
        />
      </div>
    </SettingsSectionWithTabs>
  )
}
